import { Group, MultiSelect, SegmentedControl, Select } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { SortOrder, TaskSortField, TaskStatus } from "./api";

// Все статусы FSM в порядке жизненного цикла (зеркало backend
// `TaskStatus`). Источник опций мультиселекта фильтра.
const ALL_STATUSES: readonly TaskStatus[] = [
  "queued",
  "draft",
  "planned",
  "in_progress",
  "ci_running",
  "review",
  "awaiting_plan_approval",
  "awaiting_approval",
  "done",
  "cancelled",
  "escalated",
];

const SORT_FIELDS: readonly TaskSortField[] = ["created_at", "status"];

// Панель сортировки и фильтрации очереди. Полностью контролируемая:
// текущие значения и колбэки приходят сверху (из QueuePage), компонент
// только рендерит контролы и пробрасывает изменения.
export function QueueFilters({
  statuses,
  onStatusesChange,
  sort,
  onSortChange,
  order,
  onOrderChange,
}: {
  readonly statuses: readonly TaskStatus[];
  readonly onStatusesChange: (next: TaskStatus[]) => void;
  readonly sort: TaskSortField;
  readonly onSortChange: (next: TaskSortField) => void;
  readonly order: SortOrder;
  readonly onOrderChange: (next: SortOrder) => void;
}): ReactElement {
  const { t } = useTranslation();

  const statusOptions = ALL_STATUSES.map((status) => ({
    value: status,
    label: t(`taskStatus.${status}`),
  }));
  const sortOptions = SORT_FIELDS.map((field) => ({
    value: field,
    label: t(`queueFilters.sortField.${field}`),
  }));

  return (
    <Group align="flex-end" gap="sm">
      <MultiSelect
        label={t("queueFilters.statusLabel")}
        placeholder={t("queueFilters.statusPlaceholder")}
        data={statusOptions}
        value={[...statuses]}
        onChange={(next) => {
          onStatusesChange(next);
        }}
        clearable
        searchable
      />
      <Select
        label={t("queueFilters.sortLabel")}
        data={sortOptions}
        value={sort}
        // allowDeselect={false}: клик по выбранной опции — no-op,
        // onChange выдаёт только не-null значение из data (одно из
        // TaskSortField).
        onChange={(next) => {
          onSortChange(next!);
        }}
        allowDeselect={false}
      />
      <SegmentedControl
        aria-label={t("queueFilters.orderLabel")}
        value={order}
        onChange={(next) => {
          onOrderChange(next);
        }}
        data={[
          { value: "asc", label: t("queueFilters.orderAsc") },
          { value: "desc", label: t("queueFilters.orderDesc") },
        ]}
      />
    </Group>
  );
}
